import GeminiService from './GeminiService';
import axios from 'axios';

const WholeResumeService = {
    // small helper to pace requests and avoid 429s
    _sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
    _isLowSignalExperience: (text) => {
        if (!text) return true;
        const s = String(text).trim();
        if (s.length < 40) return true; // very short
        const uniqueWords = new Set(s.toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/).filter(Boolean));
        if (uniqueWords.size < 6) return true; // too few unique words
        // obvious junk or non-professional phrases
        const junk = /(hire\s*me|just\s*hire\s*me|yo!|lol|asdf|lorem\s+ipsum)/i;
        if (junk.test(s)) return true;
        return false;
    },
    _normalizeForCompare: (t) => {
        return (t || '')
            .toLowerCase()
            .replace(/https?:\/\/\S+/g, ' ')
            .replace(/[^a-z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    },
    _tokenSet: (t) => new Set(((t || '').split(/\s+/)).filter(w => w && w.length > 2)),
    _jaccard: (a, b) => {
        const A = WholeResumeService._tokenSet(WholeResumeService._normalizeForCompare(a));
        const B = WholeResumeService._tokenSet(WholeResumeService._normalizeForCompare(b));
        if (A.size === 0 || B.size === 0) return 0;
        let inter = 0;
        for (const w of A) if (B.has(w)) inter++;
        const uni = new Set([...A, ...B]).size;
        return inter / uni;
    },
    // Function to generate the entire resume
    generateWholeResume: async (formData, characterLimits, updateStatus) => {
        try {
            let generatedData = {};
            // Generate only canonical resume sections in a fixed order
            const TARGET_FIELDS = ['title', 'summary', 'experience', 'skills', 'education', 'publications'];
            // Provide safe default limits if any are missing
            const DEFAULT_LIMITS = { title: 100, summary: 1500, experience: 1000, skills: 300, education: 500, publications: 500 };

            for (const field of TARGET_FIELDS) {
                try {
                    const limit = characterLimits?.[field] ?? DEFAULT_LIMITS[field];
                    let currentText = (formData?.[field] ?? '').toString();
                    if (field === 'experience' && WholeResumeService._isLowSignalExperience(currentText)) {
                        // Build composite context to help the model infer bullets
                        currentText = [
                            'Candidate profile context for experience bullets:',
                            `Title: ${formData?.title || ''}`,
                            `Summary: ${formData?.summary || ''}`,
                            'Skills:',
                            (formData?.skills || ''),
                            'Raw notes (may be low-signal):',
                            (formData?.experience || '')
                        ].join('\n');
                    } else if (field === 'skills' && WholeResumeService._isLowSignalExperience(currentText)) {
                        // Use broader context to infer skills when the input is junk/empty
                        currentText = [
                            'Infer core skills as bullet lines from the candidate context below:',
                            `Title: ${formData?.title || ''}`,
                            `Summary: ${formData?.summary || ''}`,
                            'Experience bullets/notes:',
                            (formData?.experience || ''),
                        ].join('\n');
                    }
                    console.debug('[WholeResumeService] start field', field, { charLimit: limit, currentLen: currentText.length });
                    updateStatus(`Generating ${field}...`);

                    const generatedText = await GeminiService.generateText(field, currentText, limit, updateStatus);
                    console.debug('[WholeResumeService] generated raw', field, { len: (generatedText||'').length, preview: (generatedText||'').slice(0,80) });

                    // For short fields like title, return immediately to keep UX snappy
                    if (field === 'title') {
                        const quickText = (generatedText || '').toString().trim();
                        generatedData[field] = quickText.length > 0 ? quickText : currentText;
                        await WholeResumeService._sleep(600);
                        continue;
                    }

                    // pace between generate and validate for longer sections
                    await WholeResumeService._sleep(1400);
                    const validatedText = await GeminiService.validateAndAdjust(field, generatedText, limit, updateStatus);
                    console.debug('[WholeResumeService] validated', field, { len: (validatedText||'').length, preview: (validatedText||'').slice(0,80) });
                    const finalText = (validatedText || '').toString().trim();
                    generatedData[field] = finalText.length > 0 ? finalText : currentText;
                    // slight gap before next field
                    await WholeResumeService._sleep(600);
                } catch (fieldErr) {
                    console.error(`[WholeResumeService] Error generating ${field}:`, fieldErr);
                    // Keep previous content for this field and continue with others
                    generatedData[field] = (formData?.[field] ?? '').toString();
                    updateStatus?.(`Keeping previous ${field} due to a temporary issue.`);
                    await WholeResumeService._sleep(400);
                }
            }

            return generatedData;
        } catch (error) {
            console.error('Error generating whole resume:', error);
            throw error;
        }
    },

    // Function to generate a specific section of the resume
    generateSection: async (section, formData, characterLimits) => {
        try {
            console.debug('[WholeResumeService] generateSection', section, { charLimit: characterLimits[section], currentLen: (formData[section]||'').toString().length });
            // For coverLetter, pass structured context rather than the raw string
            let currentText = section === 'coverLetter' ? {
                name: formData.name,
                title: formData.title,
                summary: formData.summary,
                experience: formData.experience,
                skills: formData.skills,
                education: formData.education,
                companyName: formData.companyName,
                jobDescription: formData.jobDescription,
                coverLetterBody: formData.coverLetterBody,
                applicantAddress: formData.applicantAddress,
                recipientName: formData.recipientName,
                recipientJobTitle: formData.recipientJobTitle,
            } : formData[section];

            // For experience, if input is low-signal, pass richer context (summary/skills/title)
            if (section === 'experience') {
                const raw = (formData?.experience || '').toString();
                if (WholeResumeService._isLowSignalExperience(raw)) {
                    currentText = [
                        'Candidate profile context for experience bullets:',
                        `Title: ${formData?.title || ''}`,
                        `Summary: ${formData?.summary || ''}`,
                        'Skills:',
                        (formData?.skills || ''),
                        'Raw notes (may be low-signal):',
                        raw
                    ].join('\n');
                }
            } else if (section === 'skills') {
                const raw = (formData?.skills || '').toString();
                if (WholeResumeService._isLowSignalExperience(raw)) {
                    currentText = [
                        'Infer core skills as bullet lines from the candidate context below:',
                        `Title: ${formData?.title || ''}`,
                        `Summary: ${formData?.summary || ''}`,
                        'Experience bullets/notes:',
                        (formData?.experience || ''),
                    ].join('\n');
                }
            }
            const generatedText = await GeminiService.generateText(section, currentText, characterLimits[section], () => {});
            console.debug('[WholeResumeService] section generated raw', section, { len: (generatedText||'').length, preview: (generatedText||'').slice(0,80) });

            // For short fields like title, return immediately to keep UX snappy
            if (section === 'title') {
                const quickText = (generatedText || '').toString().trim();
                return { [section]: quickText.length > 0 ? quickText : (currentText || '').toString() };
            }

            // pace between generate and validate for longer sections
            await WholeResumeService._sleep(2200);
            const validatedText = await GeminiService.validateAndAdjust(section, generatedText, characterLimits[section], () => {});
            console.debug('[WholeResumeService] section validated', section, { len: (validatedText||'').length, preview: (validatedText||'').slice(0,80) });
            const finalText = (validatedText || '').toString().trim();
            return {
                [section]: finalText.length > 0 ? finalText : (currentText || '').toString()
            };
        } catch (error) {
            console.error(`Error generating ${section}:`, error);
            throw error;
        }
    },

    // Function to generate a cover letter (also optionally fills jobDescription if it was empty and a URL was provided)
    generateCoverLetter: async (formData, updateStatus) => {
    try {
        const {
            name,
            title,
            summary,
            experience,
            skills,
            education,
            jobDescription,
            jobPostUrl,
            companyName,
            targetRole,
            applicantAddress,
            recipientName,
            recipientJobTitle,
            coverLetterBody,
            whyCompany,
            keyAchievements,
            keyRoleSkills,
        } = formData;

        updateStatus('Generating cover letter...');

        // Helper to compose a concise advert from structured pieces
        const composeAdvert = (sum, resp, reqs, fallbackText) => {
            try {
                const parts = [];
                if (sum) parts.push(`Summary:\n${String(sum).trim()}`);
                if (Array.isArray(resp) && resp.length) {
                    parts.push(`Responsibilities:\n- ${resp.map(b => String(b).trim()).filter(Boolean).join('\n- ')}`);
                }
                if (Array.isArray(reqs) && reqs.length) {
                    parts.push(`Requirements:\n- ${reqs.map(b => String(b).trim()).filter(Boolean).join('\n- ')}`);
                }
                let out = parts.join('\n\n').trim();
                if (!out && fallbackText) {
                    // Fallback to extracted text but cap to a sane length to avoid giant pastes
                    const f = String(fallbackText);
                    out = f.length > 4000 ? f.slice(0, 4000) + '…' : f;
                }
                return out || '';
            } catch {
                const f = (fallbackText || '').toString();
                return f.length > 4000 ? f.slice(0, 4000) + '…' : f;
            }
        };

        // If both pasted jobDescription and URL are present, validate alignment
        const pasted = (jobDescription || '').trim();
        const urlVal = (jobPostUrl || '').trim();
        const hasBoth = pasted.length > 60 && urlVal.length > 7; // require meaningful pasted text
        if (hasBoth) {
            let fetchedText = '';
            let fetchedSummary = '';
            let fetchedResp = [];
            let fetchedReqs = [];
            let conflictReason = '';
            let tempId = null;
            try {
                updateStatus('Validating pasted advert vs URL…');
                const { data } = await axios.get('/api/ai/fetch-job-post', { params: { url: urlVal, save: true } });
                if (data && typeof data.text === 'string' && data.text.length > 120) {
                    fetchedText = data.text;
                    tempId = data.tempId || null;
                    if (tempId) updateStatus(`Snapshot saved (id: ${tempId}).`);
                    if (data.fetchMethod) {
                        updateStatus(`Fetched via ${(data.fetchMethod || 'unknown').toUpperCase()}`);
                    }
                    // Summarize fetched to get structured comparators and a user-friendly suggestion
                    const { data: sumData } = await axios.post('/api/ai/summarize-job-post', { text: fetchedText, tempId });
                    const raw = sumData?.choices?.[0]?.message?.content;
                    if (sumData && sumData.summarySaved) updateStatus('Summary snapshot saved.');
                    if (raw) {
                        try {
                            const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
                            fetchedSummary = (obj.summary || '').toString();
                            fetchedResp = Array.isArray(obj.responsibilities) ? obj.responsibilities.filter(Boolean) : [];
                            fetchedReqs = Array.isArray(obj.requirements) ? obj.requirements.filter(Boolean) : [];
                        } catch {}
                    }
                    // Similarity heuristic
                    const sim = WholeResumeService._jaccard(pasted, fetchedText || fetchedSummary);
                    // Meta heuristic (company/role)
                    let metaMismatch = false;
                    try {
                        const metaP = await GeminiService.extractJobMeta(pasted);
                        const metaF = await GeminiService.extractJobMeta(fetchedText);
                        const compP = (metaP?.company || '').toLowerCase().trim();
                        const compF = (metaF?.company || '').toLowerCase().trim();
                        const roleP = (metaP?.roleTitle || '').toLowerCase().trim();
                        const roleF = (metaF?.roleTitle || '').toLowerCase().trim();
                        if (compP && compF && compP !== compF) metaMismatch = true;
                        if (roleP && roleF && roleP !== roleF) metaMismatch = true || metaMismatch;
                    } catch {}

                    const lowSim = sim < 0.28; // conservative threshold
                    if (lowSim || metaMismatch) {
                        conflictReason = metaMismatch ? 'Company/role look different.' : 'Texts appear to describe different postings.';
                        const suggestedJobDescription = composeAdvert(fetchedSummary, fetchedResp, fetchedReqs, fetchedText);
                        updateStatus('Conflict detected between pasted advert and URL.');
                        return {
                            conflict: true,
                            conflictInfo: {
                                reason: conflictReason,
                                pastedPreview: pasted.slice(0, 320),
                                fetchedPreview: (fetchedText || '').slice(0, 320),
                                tempId: tempId || undefined,
                                suggestedJobDescription
                            },
                            jobPreview: {
                                summary: fetchedSummary || '',
                                responsibilities: fetchedResp || [],
                                requirements: fetchedReqs || []
                            }
                        };
                    }
                }
            } catch (e) {
                // If validation fails, continue with normal flow using pasted text.
                console.warn('[WholeResumeService] dual-input validation failed; proceeding with pasted', e?.message || e);
            }
            // If no conflict, proceed normally with pasted content as primary
        }

        // Prefer pasted jobDescription; otherwise attempt to fetch from URL
        let jobContext = pasted;
        const isBlockedStub = (txt) => {
            const s = (txt || '').toString().toLowerCase();
            if (!s.trim()) return false;
            return (
                s.includes('403') ||
                s.includes('forbidden') ||
                s.includes("couldn't auto-fetch") ||
                s.includes('could not fetch the job post') ||
                s.includes('paste the full job advert') ||
                s.includes('sign-in may be required')
            );
        };
        let jobSummary = '';
        let jobResponsibilities = [];
        let placeholderAdvert = '';
        let jobRequirements = [];
        const originallyEmptyJobDescription = !(jobDescription || '').trim() || isBlockedStub(jobDescription);
        let snapshotTempId = null;
        const hasUrl = urlVal.length > 0;
    // If a URL is provided, try to fetch to power the preview, but do not override a meaningful pasted advert
    if (hasUrl) {
            try {
                updateStatus('Opening page and fetching content…');
                const { data } = await axios.get('/api/ai/fetch-job-post', { params: { url: urlVal, save: true } });
                if (data && typeof data.text === 'string') {
                    const fetchedText = data.text;
                    if (data.tempId) {
                        snapshotTempId = data.tempId;
                        updateStatus(`Snapshot saved (id: ${data.tempId}).`);
                    }
                    if (data.fetchMethod) {
                        const v = (data.fetchMethod || 'unknown').toUpperCase();
                        updateStatus(`Fetched via ${v}${data.fetchUrl ? ` @ ${data.fetchUrl}` : ''}`);
                    }
                    // Prefer fetched usable content ONLY if pasted is empty/blocked/too short
                    const pastedIsWeak = !(pasted && pasted.length > 120) || isBlockedStub(pasted);
                    if (pastedIsWeak && fetchedText && fetchedText.length > 120 && !isBlockedStub(fetchedText)) {
                        jobContext = fetchedText;
                    }
                }
                // If we still don't have usable text or it looks like a block page, insert a placeholder and skip summarization
                if (!jobContext || jobContext.length <= 120 || isBlockedStub(jobContext)) {
                    updateStatus('Target site blocked automated fetching. Inserting a placeholder.');
                    const safeUrl = urlVal.toString();
                    placeholderAdvert = [
                        `We couldn't auto-fetch the job posting from: ${safeUrl}`,
                        '',
                        'Please paste the full job advert here to personalize your cover letter and resume alignment.',
                        'In the meantime, a general cover letter has been generated based on your resume.'
                    ].join('\n');
                    jobContext = '';
                }

                if (jobContext && jobContext.length > 120) {
                    updateStatus('Summarizing and extracting key details…');
                    try {
                        const { data: sumData } = await axios.post('/api/ai/summarize-job-post', { text: jobContext, tempId: snapshotTempId });
                        const raw = sumData && sumData.choices && sumData.choices[0] && sumData.choices[0].message && sumData.choices[0].message.content;
                        if (sumData && sumData.summarySaved) {
                            updateStatus('Summary snapshot saved.');
                        }
                        if (raw) {
                            try {
                                const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                jobSummary = (obj.summary || '').toString();
                                jobResponsibilities = Array.isArray(obj.responsibilities) ? obj.responsibilities.filter(Boolean) : [];
                                jobRequirements = Array.isArray(obj.requirements) ? obj.requirements.filter(Boolean) : [];
                                if (jobSummary) {
                                    const prev = jobSummary.length > 240 ? jobSummary.slice(0, 237) + '…' : jobSummary;
                                    updateStatus(`Summary preview: ${prev}`);
                                }
                            } catch {}
                        }
                    } catch (sumErr) {
                        console.error('[WholeResumeService] summarize error', sumErr);
                        updateStatus('Summarization step encountered an issue. Using extracted text.');
                    }
                }
            } catch (e) {
                console.error('[WholeResumeService] fetch error', e);
                updateStatus('Could not fetch the job post automatically. Generating a resume-based cover letter…');
                if (originallyEmptyJobDescription) {
                    const safeUrl = urlVal.toString();
                    placeholderAdvert = [
                        `We couldn't auto-fetch the job posting from: ${safeUrl}`,
                        '',
                        'Please paste the full job advert here to personalize your cover letter and resume alignment.',
                        'In the meantime, a general cover letter has been generated based on your resume.'
                    ].join('\n');
                }
            }
        }

        // Try to auto-extract meta (company, role, recipient) from the actual job text and PREFER it when present
        let inferredCompany = companyName;
        let inferredRole = targetRole;
        let inferredRecipient = recipientName;
        let inferredRecipientTitle = recipientJobTitle;
        // If a URL is provided but no usable job text, blank out fields to avoid hallucinations
        if (urlVal && !jobContext) {
            inferredCompany = '';
            inferredRole = '';
            inferredRecipient = '';
            inferredRecipientTitle = '';
        }
        // If we do have job text, prefer metadata extracted from it over any prefilled profile targets
        if (jobContext) {
            try {
                updateStatus('Analyzing job post...');
                const meta = await GeminiService.extractJobMeta(jobContext);
                if (meta && typeof meta === 'object') {
                    if (meta.company && meta.company.trim()) {
                        if (inferredCompany && inferredCompany.trim() && inferredCompany.trim().toLowerCase() !== meta.company.trim().toLowerCase()) {
                            updateStatus(`Using company from job post: ${meta.company.trim()}`);
                        }
                        inferredCompany = meta.company.trim();
                    }
                    if (meta.roleTitle && meta.roleTitle.trim()) {
                        if (inferredRole && inferredRole.trim() && inferredRole.trim().toLowerCase() !== meta.roleTitle.trim().toLowerCase()) {
                            updateStatus(`Using role from job post: ${meta.roleTitle.trim()}`);
                        }
                        inferredRole = meta.roleTitle.trim();
                    }
                    // Only fill recipient fields if not already provided by the user
                    if (!inferredRecipient && meta.recipientName && meta.recipientName.trim()) {
                        inferredRecipient = meta.recipientName.trim();
                    }
                }
            } catch {}
        }

        const context = {
            name,
            title,
            summary,
            experience,
            skills,
            education,
            companyName: inferredCompany,
            targetRole: inferredRole,
            jobDescription: jobContext,
            jobPostUrl: urlVal,
            jobSummary,
            jobResponsibilities,
            jobRequirements,
            coverLetterBody,
            applicantAddress,
            recipientName: inferredRecipient,
            recipientJobTitle: inferredRecipientTitle,
            whyCompany,
            keyAchievements,
            keyRoleSkills,
        };

    const COVER_LETTER_LIMIT = 1200;
    const generatedCoverLetter = await GeminiService.generateText('coverLetter', context, COVER_LETTER_LIMIT, updateStatus);
    const validatedCoverLetter = await GeminiService.validateAndAdjust('coverLetter', generatedCoverLetter, COVER_LETTER_LIMIT, updateStatus);

        let composedJobAdvert = '';
        if (jobContext) {
            composedJobAdvert = composeAdvert(jobSummary, jobResponsibilities, jobRequirements, jobContext);
        } else if (!jobContext && placeholderAdvert) {
            composedJobAdvert = placeholderAdvert;
        }

    const jobPreview = {
            summary: jobSummary || '',
            responsibilities: jobResponsibilities || [],
            requirements: jobRequirements || [],
            companyName: inferredCompany || '',
            roleTitle: inferredRole || '',
            recipientName: inferredRecipient || '',
            recipientJobTitle: inferredRecipientTitle || ''
        };
    return { coverLetter: validatedCoverLetter, jobDescription: composedJobAdvert, jobPreview, inferredCompany, inferredRole };
    } catch (error) {
        console.error('Error generating cover letter:', error);
        throw error;
    }
}

};

export default WholeResumeService;
